import React from "react";

import { faLink } from "@fortawesome/sharp-regular-svg-icons/faLink";
import { faCircleUser } from "@fortawesome/sharp-solid-svg-icons/faCircleUser";
import { faInfoCircle } from "@fortawesome/sharp-solid-svg-icons/faInfoCircle";

import { variantBackgroundColor } from "~styles/common/variant.backgroundColor.css";

import { Box } from "~components/Box";
import { IconFontAwesome } from "~components/IconFontAwesome";
import { StoryblokEditable } from "~components/StoryblokEditable";
import { ShowChildren } from "~components/VisibilityCheck/ShowChildren";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { BoxProps } from "~components/Box";
import type { StoryblokBlok } from "~types/storyblok.types";

const ICON_MAP = {
  faLink,
  faCircleUser,
} as const;

type IconMapKey = keyof typeof ICON_MAP;

export interface AnimLayerInfoCardProps extends StoryblokBlok, BoxProps {
  title?: string;
  description?: string;
  badge?: string;
  iconLeft?: IconMapKey;
}

export function AnimLayerInfoCard({
  iconLeft,
  title,
  description,
  badge,
  ...rest
}: AnimLayerInfoCardProps) {
  const dynamicIconLeft = iconLeft && (ICON_MAP[iconLeft] as IconDefinition);

  return (
    <StoryblokEditable {...rest}>
      <Box
        className={variantBackgroundColor.background_white}
        borderRadius="0.75"
        paddingX="phoneSpacing2"
        paddingY="phoneSpacing1.5"
        border="extraLowContrast"
        marginBottom="phoneSpacing1.5"
        {...rest}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <ShowChildren when={dynamicIconLeft}>
              <IconFontAwesome
                icon={dynamicIconLeft!}
                size="xs"
                marginRight="phoneSpacing1"
              />
            </ShowChildren>
            <Box
              textAppearance={description ? "phone_text_md" : "phone_text_xs"}
              fontWeight={description ? "semibold" : "normal"}
            >
              {title}
            </Box>
          </Box>
          <Box display="flex" gap="phoneSpacing1" alignItems="center">
            <ShowChildren when={badge}>
              <Box
                fontWeight="semibold"
                textAppearance="phone_text_xxs"
                className={variantBackgroundColor.background_mockUiCard}
                borderRadius="xs"
                paddingX="phoneSpacing0"
                paddingY="phoneSpacingMin"
              >
                {badge}
              </Box>
            </ShowChildren>
            <IconFontAwesome
              icon={faInfoCircle as IconDefinition}
              size="xs"
              color="accent_base"
            />
          </Box>
        </Box>
        <ShowChildren when={description}>
          <Box textAppearance="phone_text_sm" marginTop="phoneSpacing1">
            {description}
          </Box>
        </ShowChildren>
      </Box>
    </StoryblokEditable>
  );
}
